import React, { useEffect, useState } from 'react';
import { Boxes, FileCode } from 'lucide-react';
import { api } from '../../services/authenticatedApi';

interface Project {
  name: string;
  description: string;
  languages: string[];
  findingsCount: number;
  filesCount: number | null;
  isRoot: boolean;
  kinds: string[];
}

interface Breakdown {
  is_monorepo: boolean;
  project_count: number;
  projects: Project[];
}

export const ProjectsPanel: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const [data, setData] = useState<Breakdown | null>(null);

  useEffect(() => {
    api
      .get<Breakdown>(`/repositories/${repositoryId}/projects`)
      .then((res) => setData(res.data))
      .catch(() => setData(null));
  }, [repositoryId]);

  // Only render for monorepos — single-project repos add no signal here.
  if (!data || !data.is_monorepo || data.projects.length === 0) return null;

  return (
    <section className="mt-6 p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2 mb-1">
        <Boxes className="w-5 h-5 text-teal-600 dark:text-teal-400" />
        <h2 className="font-semibold text-gray-900 dark:text-white">Key projects</h2>
        <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
          monorepo · {data.project_count} units
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Autar broke this repository into logical units and analyzed each one.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {data.projects.map((p) => (
          <div key={p.name} className="p-3 rounded-lg border border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2 mb-1">
              <FileCode className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
                {p.name}
              </span>
              {p.isRoot && (
                <span className="text-[10px] text-gray-400 uppercase tracking-wide">root</span>
              )}
            </div>
            {p.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                {p.description}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {p.languages.map((l) => (
                <span
                  key={l}
                  className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                >
                  {l}
                </span>
              ))}
              {p.findingsCount > 0 && (
                <span className="text-amber-600 dark:text-amber-400">
                  {p.findingsCount} findings
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
